/**
 * Military Relocation Auto Loan Calculator Utilities
 * 
 * This file contains calculation functions for Australian Defence Force (ADF) 
 * members and veterans who are relocating and need auto financing at their new location.
 */

// Define the input interface for the calculator
export interface MilitaryRelocationLoanInputs {
  vehiclePrice: number;           // Purchase price of the vehicle
  downPayment: number;            // Initial deposit
  loanTerm: number;               // Loan term in months
  interestRate: number;           // Annual interest rate (%)
  serviceStatus: string;          // 'active', 'veteran', 'reserve', or 'civilian'
  isOverseasDeployment: boolean;  // Whether relocating from overseas deployment
  isPostingRelocation: boolean;   // Whether relocation is due to military posting
  relocationDistance: number;     // Distance of relocation in kilometers
  relocationType: string;         // 'interstate', 'regional', 'metropolitan', 'overseas'
  balloonPayment: number;         // Optional balloon payment at end
  applicationFee: number;         // Loan application fee
  annualFee: number;              // Annual loan maintenance fee
  includeDHASubsidy: boolean;     // Whether to include Defence Housing Australia subsidy
}

// Define the output interface
export interface MilitaryRelocationLoanResult {
  monthlyPayment: number;
  totalInterest: number;
  totalFees: number;
  totalCost: number;
  loanAmount: number;
  amortizationSchedule: AmortizationRow[];
  discountedRate: number;
  rateDiscount: number;
  feeDiscount: number;
  originalRate: number;
  eligibleBenefits: string[];
  comparisonRate: number;
  relocationBenefits: RelocationBenefits;
}

// Define the amortization row interface
export interface AmortizationRow {
  month: number;
  payment: number;
  principal: number;
  interest: number;
  balance: number;
  fees: number;
}

// Define the relocation benefits interface
export interface RelocationBenefits {
  relocationAllowance: number;     // One-time relocation allowance
  vehicleTransportSavings: number; // Savings on vehicle transport
  dhaSubsidy: number;              // Defence Housing Australia subsidy
  totalRelocationBenefits: number; // Total relocation-related benefits
  effectiveLoanAmount: number;     // Loan amount after applying relocation benefits
}

// Constants for ADF and veteran benefits
const ADF_BENEFITS = {
  // Rate discounts based on service status
  RATE_DISCOUNTS: {
    active: 0.50,    // 0.50% off for active service members
    veteran: 0.35,   // 0.35% off for veterans
    reserve: 0.25,   // 0.25% off for reserves
    civilian: 0.0    // No discount for civilians
  },
  
  // Additional discount for posting relocation
  POSTING_RELOCATION_DISCOUNT: 0.15,  // Additional 0.15% off for posting relocation
  
  // Overseas deployment additional discount
  OVERSEAS_DEPLOYMENT_DISCOUNT: 0.25, // Additional 0.25% off for overseas deployment returns
  
  // Fee discounts
  APPLICATION_FEE_DISCOUNT: 0.5,      // 50% off application fee for eligible members
  
  // Relocation benefits
  RELOCATION_ALLOWANCE: {
    interstate: 1500,        // $1,500 for interstate moves
    regional: 1000,          // $1,000 for regional moves
    metropolitan: 750,       // $750 for metropolitan moves
    overseas: 2500           // $2,500 for overseas returns
  },
  
  // Vehicle transport benefits
  VEHICLE_TRANSPORT_RATES: {
    interstate: 0.75,        // $0.75 per km for interstate
    regional: 0.60,          // $0.60 per km for regional
    metropolitan: 0.45,      // $0.45 per km for metropolitan
    overseas: 0              // Not applicable for overseas (handled separately)
  },
  
  // Overseas vehicle return flat rate
  OVERSEAS_VEHICLE_RETURN: 3500,      // $3,500 flat rate for overseas vehicle return
  
  // DHA monthly subsidy amount (if applicable)
  DHA_MONTHLY_SUBSIDY: 250            // $250 monthly payment assistance via DHA
};

/**
 * Calculate military/veteran relocation auto loan details
 * 
 * @param inputs Object containing all required inputs for calculation
 * @returns MilitaryRelocationLoanResult with calculation results and benefits
 */
export function calculateMilitaryRelocationLoan(inputs: MilitaryRelocationLoanInputs): MilitaryRelocationLoanResult {
  const {
    vehiclePrice,
    downPayment,
    loanTerm,
    interestRate,
    serviceStatus,
    isOverseasDeployment,
    isPostingRelocation,
    relocationDistance,
    relocationType,
    balloonPayment,
    applicationFee,
    annualFee,
    includeDHASubsidy
  } = inputs;

  // Calculate base loan amount
  const loanAmount = vehiclePrice - downPayment;
  
  // Store original rate for comparison
  const originalRate = interestRate;
  
  // Calculate rate discount based on service status and other factors
  let rateDiscount = ADF_BENEFITS.RATE_DISCOUNTS[serviceStatus as keyof typeof ADF_BENEFITS.RATE_DISCOUNTS] || 0;
  
  // Add posting relocation discount if applicable
  if (isPostingRelocation) {
    rateDiscount += ADF_BENEFITS.POSTING_RELOCATION_DISCOUNT;
  }
  
  // Add overseas deployment discount if applicable
  if (isOverseasDeployment) {
    rateDiscount += ADF_BENEFITS.OVERSEAS_DEPLOYMENT_DISCOUNT;
  }
  
  // Apply the discounted rate (ensure it doesn't go below 0)
  const discountedRate = Math.max(0, interestRate - rateDiscount);
  
  // Calculate fee discount
  let appFeeWithDiscount = applicationFee;
  let annualFeeWithDiscount = annualFee;
  let feeDiscount = 0;
  
  // Apply fee discounts based on service status
  if (serviceStatus !== 'civilian') {
    // Apply application fee discount
    feeDiscount += applicationFee * ADF_BENEFITS.APPLICATION_FEE_DISCOUNT;
    appFeeWithDiscount = applicationFee - (applicationFee * ADF_BENEFITS.APPLICATION_FEE_DISCOUNT);
    
    // Waive application fee entirely for posting relocations
    if (isPostingRelocation) {
      feeDiscount += appFeeWithDiscount;
      appFeeWithDiscount = 0;
    }
  }
  
  // Calculate relocation benefits
  const relocationBenefits = calculateRelocationBenefits(
    relocationType,
    relocationDistance,
    isOverseasDeployment,
    loanTerm,
    includeDHASubsidy
  );
  
  // Calculate effective loan amount after relocation benefits
  const effectiveLoanAmount = Math.max(0, loanAmount - relocationBenefits.totalRelocationBenefits);
  
  // Convert annual rate to monthly
  const monthlyRate = discountedRate / 100 / 12;
  
  // Calculate monthly payment (based on effective loan amount)
  const monthlyPayment = calculateMonthlyPayment(effectiveLoanAmount, monthlyRate, loanTerm, balloonPayment);
  
  // Generate amortization schedule
  const amortizationSchedule = generateAmortizationSchedule(
    effectiveLoanAmount, 
    monthlyRate, 
    loanTerm, 
    monthlyPayment, 
    balloonPayment, 
    annualFeeWithDiscount
  );
  
  // Calculate total interest
  const totalInterest = amortizationSchedule.reduce((sum, month) => sum + month.interest, 0);
  
  // Calculate total fees
  const totalFees = appFeeWithDiscount + (annualFeeWithDiscount * Math.ceil(loanTerm / 12));
  
  // Calculate comparison rate
  const comparisonRate = calculateComparisonRate(
    effectiveLoanAmount, 
    discountedRate, 
    loanTerm / 12, 
    appFeeWithDiscount, 
    annualFeeWithDiscount
  );
  
  // Determine eligible benefits
  const eligibleBenefits = determineEligibleBenefits(
    serviceStatus, 
    isPostingRelocation,
    isOverseasDeployment,
    relocationType
  );
  
  // Return the result
  return {
    monthlyPayment,
    totalInterest,
    totalFees,
    totalCost: effectiveLoanAmount + totalInterest + totalFees,
    loanAmount,
    amortizationSchedule,
    discountedRate,
    rateDiscount,
    feeDiscount,
    originalRate,
    eligibleBenefits,
    comparisonRate,
    relocationBenefits
  };
}

/**
 * Calculate relocation benefits
 */
function calculateRelocationBenefits(
  relocationType: string,
  relocationDistance: number,
  isOverseasDeployment: boolean,
  loanTerm: number,
  includeDHASubsidy: boolean
): RelocationBenefits {
  // Calculate relocation allowance
  const relocationAllowance = 
    ADF_BENEFITS.RELOCATION_ALLOWANCE[relocationType as keyof typeof ADF_BENEFITS.RELOCATION_ALLOWANCE] || 0;
  
  // Calculate vehicle transport savings
  let vehicleTransportSavings = 0;
  
  if (isOverseasDeployment) {
    vehicleTransportSavings = ADF_BENEFITS.OVERSEAS_VEHICLE_RETURN;
  } else {
    const ratePerKm = 
      ADF_BENEFITS.VEHICLE_TRANSPORT_RATES[relocationType as keyof typeof ADF_BENEFITS.VEHICLE_TRANSPORT_RATES] || 0;
    vehicleTransportSavings = relocationDistance * ratePerKm;
  }
  
  // Calculate DHA subsidy if applicable
  const dhaSubsidy = includeDHASubsidy ? ADF_BENEFITS.DHA_MONTHLY_SUBSIDY * loanTerm : 0;
  
  // Calculate total relocation benefits
  const totalRelocationBenefits = relocationAllowance + vehicleTransportSavings + dhaSubsidy;
  
  // Calculate effective loan amount
  const effectiveLoanAmount = Math.max(0, 0); // Placeholder - actual calculation is done in main function
  
  return {
    relocationAllowance,
    vehicleTransportSavings,
    dhaSubsidy,
    totalRelocationBenefits,
    effectiveLoanAmount
  };
}

/**
 * Calculate monthly payment
 */
function calculateMonthlyPayment(
  principal: number, 
  monthlyRate: number, 
  term: number, 
  balloonPayment: number
): number {
  if (monthlyRate === 0) {
    // If rate is 0%, it's a simple division plus balloon adjustment
    return (principal - balloonPayment) / term;
  }
  
  // Standard formula with balloon adjustment
  return (
    (principal * (monthlyRate * Math.pow(1 + monthlyRate, term))) / 
    (Math.pow(1 + monthlyRate, term) - 1)
  ) - (
    (balloonPayment * monthlyRate) / 
    (Math.pow(1 + monthlyRate, term) - 1)
  );
}

/**
 * Generate amortization schedule
 */
function generateAmortizationSchedule(
  principal: number,
  monthlyRate: number,
  totalPeriods: number,
  monthlyPayment: number,
  balloonPayment: number,
  annualFee: number
): AmortizationRow[] {
  let balance = principal;
  const schedule: AmortizationRow[] = [];
  
  for (let month = 1; month <= totalPeriods; month++) {
    const interestPayment = balance * monthlyRate;
    let principalPayment = monthlyPayment - interestPayment;
    
    // For the last payment, add the balloon amount
    if (month === totalPeriods) {
      principalPayment += balloonPayment;
    }
    
    balance -= principalPayment;
    
    // Add annual fee on 12-month intervals (starting from month 12)
    const fees = (month % 12 === 0) ? annualFee : 0;
    
    schedule.push({
      month,
      payment: month === totalPeriods ? monthlyPayment + balloonPayment : monthlyPayment,
      principal: principalPayment,
      interest: interestPayment,
      balance: Math.max(0, balance),
      fees
    });
  }
  
  return schedule;
}

/**
 * Calculate comparison rate based on Australian rules
 */
export function calculateComparisonRate(
  loanAmount: number,
  interestRate: number,
  loanTerm: number,
  applicationFee: number,
  annualFee: number
): number {
  // Simplified comparison rate calculation
  const totalFees = applicationFee + (annualFee * loanTerm);
  const feePercentage = (totalFees / loanAmount) * 100;
  
  // Annualized fee percentage
  const annualizedFeePercentage = feePercentage / loanTerm;
  
  // Add the annualized fee percentage to the interest rate
  return interestRate + annualizedFeePercentage;
}

/**
 * Determine eligible benefits based on service profile and relocation factors
 */
function determineEligibleBenefits(
  serviceStatus: string,
  isPostingRelocation: boolean,
  isOverseasDeployment: boolean,
  relocationType: string
): string[] {
  const benefits: string[] = [];
  
  // Service status benefits
  if (serviceStatus === 'active') {
    benefits.push('ADF Active Service Rate Discount');
    benefits.push('50% Application Fee Discount');
  } else if (serviceStatus === 'veteran') {
    benefits.push('Veteran Rate Discount');
    benefits.push('50% Application Fee Discount');
  } else if (serviceStatus === 'reserve') {
    benefits.push('ADF Reserve Rate Discount');
    benefits.push('50% Application Fee Discount');
  }
  
  // Posting relocation benefits
  if (isPostingRelocation) {
    benefits.push('Posting Relocation Rate Discount');
    benefits.push('Application Fee Waiver');
    benefits.push('Relocation Allowance Eligibility');
  }
  
  // Overseas deployment benefits
  if (isOverseasDeployment) {
    benefits.push('Overseas Deployment Return Rate Discount');
    benefits.push('Overseas Vehicle Return Allowance');
  }
  
  // Add relocation type-specific benefits
  if (relocationType === 'interstate') {
    benefits.push('Interstate Relocation Allowance');
  } else if (relocationType === 'regional') {
    benefits.push('Regional Relocation Allowance');
  } else if (relocationType === 'metropolitan') {
    benefits.push('Metropolitan Relocation Allowance');
  } else if (relocationType === 'overseas') {
    benefits.push('Overseas Return Relocation Allowance');
  }
  
  return benefits;
}

/**
 * Format currency for display
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}

/**
 * Format percentage for display
 */
export function formatPercentage(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value / 100);
} 